import { type ConditionFilterType } from '~/constants/automationRuleForm';
import { type OperatorString } from '~/constants/operators';

type JSONAutomationRule = {
  account_object_id: string;
  description?: string;
  filter_group: JSONFilterGroup;
  name: string;
  object_id?: string;
  parcel_template_object_id: string;
  rule_type: string;
  sender_address_id?: number;
  service_level_token: string;
  tag_ids?: string[];
};

type JSONFilterGroup = {
  filter_criteria: JSONFilterCriteria[];
};

type JSONFilterClause = {
  filter_criteria: JSONFilterCriteria[];
  operator: string;
};

type JSONFilter = {
  comparator: string;
  field: string;
  value: JSONFilterValue;
};

type JSONFilterCriteria = (JSONFilter | JSONFilterClause)[];

type JSONFilterValue = {
  enum: string;
  value: string;
};

type BaseFormCondition = {
  filterGroup: ConditionFilterType;
  operator: OperatorString;
};

type WeightFormCondition = {
  units: string;
  weight: number;
} & BaseFormCondition;

type ItemNameFormCondition = {
  productName: string;
} & BaseFormCondition;

type SkuFormCondition = {
  sku: string;
} & BaseFormCondition;

type OrderItemQuantityFormCondition = {
  orderItemQuantity: number;
} & BaseFormCondition;

type ShippingDestinationFormCondition = {
  countries?: string[];
  country?: string;
  shippingDestination: string;
  shippingRuleType?: string;
  states?: string[];
  zipcodes?: string;
} & BaseFormCondition;

type DefaultFormCondition = {
  filterGroup: ConditionFilterType;
};

type EmptyFormConditions = {
  [ConditionFilterType.DEFAULT]: DefaultFormCondition;
  [ConditionFilterType.ITEM_SKU]: SkuFormCondition;
  [ConditionFilterType.ORDER_ITEM_QUANTITY]: OrderItemQuantityFormCondition;
  [ConditionFilterType.PRODUCT_NAME]: ItemNameFormCondition;
  [ConditionFilterType.SHIPPING_COUNTRIES]: ShippingDestinationFormCondition;
  [ConditionFilterType.SHIPPING_DESTINATION]: ShippingDestinationFormCondition;
  [ConditionFilterType.SHIPPING_STATES]: ShippingDestinationFormCondition;
  [ConditionFilterType.SHIPPING_ZIPCODES]: ShippingDestinationFormCondition;
  [ConditionFilterType.WEIGHT]: WeightFormCondition;
};

// TODO: correct types for rule
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toJSON = (rule: any): Maybe<JSONAutomationRule> => {
  if (!rule) return null;

  const {
    accountObjectId,
    description,
    filterGroup,
    name,
    objectId,
    parcelTemplateId,
    ruleType,
    senderAddressId,
    serviceLevelToken,
    tagId,
  } = rule;

  const formattedRule = {
    account_object_id: accountObjectId || null,
    description: description || null, // empty strings should be sent as null
    filter_group: filterGroupToJSON(filterGroup),
    name,
    object_id: objectId || null,
    parcel_template_object_id: parcelTemplateId,
    rule_type: ruleType,
    sender_address_id: senderAddressId || null,
    service_level_token: serviceLevelToken || null,
    tag_id: tagId || null,
  };

  return formattedRule;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterGroupToJSON = (filterGroup: any): JSONFilterGroup => {
  const { filterCriteria } = filterGroup;
  return {
    filter_criteria: filterCriteriaToJSON(filterCriteria),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterCriteriaToJSON = (filterCriteria: any): JSONFilterCriteria[] =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterCriteria.map((filterCriterion: any) => {
    if (filterCriterion.operator) return filterClauseToJSON(filterCriterion);
    else return filterCriterion as JSONFilter;
  });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filterClauseToJSON = (filterClause: any): JSONFilterClause => {
  const { filterCriteria, operator } = filterClause;
  return {
    filter_criteria: filterCriteriaToJSON(filterCriteria),
    operator,
  };
};

export { EmptyFormConditions, JSONAutomationRule, toJSON };
